import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	Button,
	Divider,
	Flex,
	Modal,
	ModalBody,
	ModalContent,
	ModalOverlay,
	Spinner,
	Text,
	VStack,
} from 'src/components/ui';
import { MdMailOutline, MdCheckCircle, MdErrorOutline, MdWarningAmber, MdSupportAgent } from 'react-icons/md';
import { IPublishedCreative, IFailedCreative } from 'src/lib/schemas/campaign/newFlowCampaign';

export type PublishingModalStep = 'checking-credits' | 'credits-ok' | 'publishing' | 'completed' | 'unexpected-error';

interface PublishResult {
	publishedCreatives: IPublishedCreative[];
	failedCreatives: IFailedCreative[];
}

interface PublishingConfirmationModalProps {
	isOpen: boolean;
	onClose: () => void;
	step: PublishingModalStep;
	publishResult?: PublishResult | null;
}

const PublishingConfirmationModal: FC<PublishingConfirmationModalProps> = ({
	isOpen,
	onClose,
	step,
	publishResult,
}) => {
	const navigate = useNavigate();

	const handleSeeCampaigns = () => {
		onClose();
		navigate('/projects/campaigns');
	};

	const renderCheckingCredits = () => (
		<>
			<Spinner
				thickness="4px"
				speed="1s"
				emptyColor="gray.200"
				color="blue.500"
				size="xl"
			/>
			<Text fontWeight="bold" fontSize="xl">
				Checking Credits...
			</Text>
			<Text fontSize="sm" color="gray.500">
				Verifying your account has enough credits to publish.
			</Text>
		</>
	);

	const renderCreditsOk = () => (
		<>
			<Box
				as={MdCheckCircle as any}
				boxSize="48px"
				color="green.400"
			/>
			<Text fontWeight="bold" fontSize="xl">
				Credits Verified
			</Text>
			<Text fontSize="sm" color="gray.500">
				Everything looks good! Starting publication...
			</Text>
		</>
	);

	const renderPublishing = () => (
		<>
			<Spinner
				thickness="4px"
				speed="1s"
				emptyColor="gray.200"
				color="blue.500"
				size="xl"
			/>

			<Text fontWeight="bold" fontSize="xl">
				Publishing Your Content...
			</Text>

			<Divider />

			<VStack spacing={2}>
				<Text fontSize="sm" color="gray.500">
					We're working on publishing your content.
				</Text>
				<Text fontSize="sm" color="gray.500">
					This process usually takes a few minutes.
				</Text>
			</VStack>

			<Box
				bg="blue.50"
				borderRadius="lg"
				px={5}
				py={4}
				w="full"
			>
				<VStack spacing={2}>
					<Box
						as={MdMailOutline as any}
						boxSize="28px"
						color="blue.500"
						mt={1}
					/>
					<Text fontSize="sm" color="blue.700">
						We'll notify you via email once it's ready!
					</Text>
					<Text fontSize="sm" color="blue.700" fontWeight="medium">
						Thank you for your patience!
					</Text>
				</VStack>
			</Box>

			{renderButtons()}
		</>
	);

	const renderCompleted = () => {
		const published = publishResult?.publishedCreatives || [];
		const failed = publishResult?.failedCreatives || [];
		const allSuccess = failed.length === 0 && published.length > 0;
		const allFailed = published.length === 0 && failed.length > 0;

		return (
			<>
				{allSuccess && (
					<Box as={MdCheckCircle as any} boxSize="48px" color="green.400" />
				)}
				{allFailed && (
					<Box as={MdErrorOutline as any} boxSize="48px" color="red.400" />
				)}
				{!allSuccess && !allFailed && published.length > 0 && (
					<Box as={MdWarningAmber as any} boxSize="48px" color="orange.400" />
				)}

				<Text fontWeight="bold" fontSize="xl">
					{allSuccess && 'Published Successfully!'}
					{allFailed && 'Publication Failed'}
					{!allSuccess && !allFailed && published.length > 0 && 'Partially Published'}
				</Text>

				<Divider />

				{published.length > 0 && (
					<Box
						bg="green.50"
						borderRadius="lg"
						px={5}
						py={3}
						w="full"
					>
						<Text fontSize="sm" color="green.700" fontWeight="medium">
							{published.length} {published.length === 1 ? 'creative' : 'creatives'} published successfully
						</Text>
					</Box>
				)}

				{failed.length > 0 && (
					<Box
						bg="red.50"
						borderRadius="lg"
						px={5}
						py={3}
						w="full"
					>
						<Text fontSize="sm" color="red.700" fontWeight="medium">
							{failed.length} {failed.length === 1 ? 'creative' : 'creatives'} failed to publish
						</Text>
						<VStack spacing={1} mt={2} align="start">
							{failed.slice(0, 5).map((fc, idx) => (
								<Text key={idx} fontSize="xs" color="red.600">
									{fc.channel ? `${fc.channel}: ` : ''}
									{fc.errors?.[0]?.message || 'Unknown error'}
								</Text>
							))}
							{failed.length > 5 && (
								<Text fontSize="xs" color="red.500">
									...and {failed.length - 5} more
								</Text>
							)}
						</VStack>
					</Box>
				)}

				<Box
					bg="blue.50"
					borderRadius="lg"
					px={5}
					py={3}
					w="full"
				>
					<Flex align="center" gap={2}>
						<Box as={MdMailOutline as any} boxSize="18px" color="blue.500" flexShrink={0} />
						<Text fontSize="sm" color="blue.700">
							Check your email for the full publication details.
						</Text>
					</Flex>
				</Box>

				{renderButtons()}
			</>
		);
	};

	const renderUnexpectedError = () => (
		<>
			<Box
				as={MdSupportAgent as any}
				boxSize="48px"
				color="orange.400"
			/>

			<Text fontWeight="bold" fontSize="xl">
				We're on it!
			</Text>

			<Divider />

			<VStack spacing={2}>
				<Text fontSize="sm" color="gray.600">
					Something unexpected happened while publishing your campaign. Our team has been notified and will review your case shortly.
				</Text>
				<Text fontSize="sm" color="gray.600">
					We'll reach out to you as soon as possible to help get your content published.
				</Text>
			</VStack>

			<Box
				bg="orange.50"
				borderRadius="lg"
				px={5}
				py={4}
				w="full"
			>
				<VStack spacing={2}>
					<Box
						as={MdMailOutline as any}
						boxSize="28px"
						color="orange.500"
						mt={1}
					/>
					<Text fontSize="sm" color="orange.700">
						You'll receive an email with updates on your campaign.
					</Text>
					<Text fontSize="sm" color="orange.700" fontWeight="medium">
						No action needed on your end!
					</Text>
				</VStack>
			</Box>

			<Box
				bg="blue.50"
				borderRadius="lg"
				px={5}
				py={3}
				w="full"
			>
				<VStack spacing={1}>
					<Text fontSize="sm" color="blue.700" fontWeight="medium">
						While you wait, there's plenty to do!
					</Text>
					<Text fontSize="xs" color="blue.600">
						Create new campaigns, explore our tools, or use Fusion Copilot to design logos, build websites, and much more.
					</Text>
				</VStack>
			</Box>

			{renderButtons()}
		</>
	);

	const renderButtons = () => (
		<Flex
			gap={3}
			w="full"
			direction={{ base: 'column', md: 'row' }}
			pt={2}
		>
			<Button
				onClick={onClose}
				flex={1}
				variant="outline"
				size="md"
			>
				Close
			</Button>
			<Button
				flex={1}
				variant="orangeSolid"
				onClick={handleSeeCampaigns}
				size="md"
			>
				See campaigns
			</Button>
		</Flex>
	);

	return (
		<Modal isOpen={isOpen} onClose={() => {}} size="md" isCentered>
			<ModalOverlay zIndex={10500} />
			<ModalContent
				my="auto"
				borderRadius="xl"
				overflow="hidden"
				containerProps={{
					zIndex: 10500,
				}}
			>
				<ModalBody py={10} px={8}>
					<VStack spacing={5} align="center" textAlign="center">
						{step === 'checking-credits' && renderCheckingCredits()}
						{step === 'credits-ok' && renderCreditsOk()}
						{step === 'publishing' && renderPublishing()}
						{step === 'completed' && renderCompleted()}
						{step === 'unexpected-error' && renderUnexpectedError()}
					</VStack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

export default PublishingConfirmationModal;
